(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarsePointer = window.matchMedia('(pointer: coarse)');
  const recentKey = 'culm-recent-products';
  const preferenceKey = 'culm-ai-preferences';
  const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const history = [];

  const routes = {
    shop: '/collections/all', search: '/search', journal: '/blogs/journal',
    materials: '/pages/materials-and-making', size: '/pages/size-guide', faq: '/pages/faq',
    contact: '/pages/contact', shipping: '/pages/shipping-policy', returns: '/pages/returns-refunds',
    account: '/account'
  };

  const safeJson = (key, fallback) => {
    try { const parsed = JSON.parse(localStorage.getItem(key) || 'null'); return parsed ?? fallback; } catch (_) { return fallback; }
  };
  const storeJson = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const panel = () => document.querySelector('[data-culm-ai]');
  const featureEnabled = (name) => panel()?.dataset[name] !== 'false';
  const readRecent = () => { if (!featureEnabled('enableMemory')) return []; const value = safeJson(recentKey, []); return Array.isArray(value) ? value.slice(0, 6) : []; };
  const readPreferences = () => featureEnabled('enableMemory') ? safeJson(preferenceKey, {}) : {};

  const pageContext = () => {
    const p = panel();
    const oldProduct = document.querySelector('[data-culm-product-context]');
    const legacyProduct = document.querySelector('.pdp-info[data-product-title]');
    return {
      pageType: p?.dataset.pageType || (document.body.className.match(/template-([^\s]+)/) || [])[1] || 'page',
      pathname: window.location.pathname,
      title: document.title,
      productId: p?.dataset.productId || oldProduct?.dataset.productId || '',
      product: p?.dataset.productTitle || oldProduct?.dataset.productTitle || legacyProduct?.dataset.productTitle || document.querySelector('.culm-shirt-product__title,.culm-product__title,.pdp-info h1')?.textContent.trim() || '',
      productType: p?.dataset.productType || oldProduct?.dataset.productType || '',
      price: p?.dataset.productPrice || oldProduct?.dataset.productPrice || document.querySelector('.culm-shirt-product__price,.pdp-price')?.textContent.trim() || '',
      productUrl: p?.dataset.productUrl || window.location.pathname,
      collection: p?.dataset.collectionTitle || document.querySelector('.culm-shirt-collection__title,.culm-collection__head h1')?.textContent.trim() || '',
      article: p?.dataset.articleTitle || '',
      recentProducts: readRecent(),
      preferences: readPreferences()
    };
  };

  const rememberCurrentProduct = () => {
    if (!featureEnabled('enableMemory')) return;
    const c = pageContext();
    if (!c.product || c.pageType !== 'product') return;
    const item = { title: c.product, path: c.productUrl || c.pathname, type: c.productType, price: c.price, id: c.productId };
    storeJson(recentKey, [item, ...readRecent().filter((x) => x.path !== item.path)].slice(0, 6));
  };

  const rememberPreferences = (message) => {
    if (!featureEnabled('enableMemory')) return {};
    const q = String(message || '').toLowerCase();
    const current = readPreferences();
    const fits = ['relaxed', 'regular', 'slim', 'oversized'];
    const fibres = ['hemp', 'linen', 'ramie', 'banana', 'bamboo', 'cotton'];
    const colours = ['black', 'white', 'blue', 'green', 'olive', 'brown', 'beige', 'cream', 'grey', 'gray'];
    const fit = fits.find((x) => q.includes(x));
    const fibre = fibres.find((x) => q.includes(x));
    const colour = colours.find((x) => q.includes(x));
    const next = { ...current };
    if (fit) next.fit = fit;
    if (fibre) next.fibre = fibre;
    if (colour) next.colour = colour === 'gray' ? 'grey' : colour;
    if (fit || fibre || colour) storeJson(preferenceKey, next);
    return next;
  };

  const actions = (...items) => items.filter(Boolean);
  const searchHref = (message) => `${routes.search}?q=${encodeURIComponent(String(message || '').trim())}`;
  const result = (text, links = [], products = [], source = 'CULM guidance') => ({ text, links, products, source });

  const localGuide = (message) => {
    const q = String(message || '').toLowerCase().trim();
    const c = pageContext();
    const prefs = rememberPreferences(message);
    const recent = c.recentProducts || [];

    if (!q) return result('Ask about a garment, fit, fibre, care, an occasion, your wardrobe, gifting, delivery or something in the Journal.');

    if ((q.includes('explain') || q.includes('this garment') || q.includes('this piece') || q.includes('this shirt')) && c.product) {
      const facts = [c.product, c.productType, c.price].filter(Boolean).join(' · ');
      return result(`This page records ${facts}. Ask about fit, care, styling or material details. I will not invent fibre percentages, provenance, measurements or environmental claims that are not present on the garment record.`, actions(
        { label: 'Size & fit guide', href: routes.size }, { label: 'Materials & making', href: routes.materials }
      ), [], 'Current garment record');
    }

    if (q.includes('compare') || q.includes('recent')) {
      if (recent.length < 2) return result('Open at least two product pages first. Ask CULM keeps a small recent-view list on this browser, then compares only recorded facts such as title, category and displayed price.', [{ label: 'Browse catalogue', href: routes.shop }], [], 'On-device recent history');
      return result(`Recently viewed: ${recent.map((x) => [x.title, x.type, x.price].filter(Boolean).join(' · ')).join(' | ')}. Open each garment record for fibre, measurements, construction and care before deciding.`, recent.map((x) => ({ label: x.title, href: x.path })), [], 'On-device recent history');
    }

    if (q.includes('fit') || q.includes('size') || q.includes('measurement') || q.includes('too big') || q.includes('too small')) {
      const saved = prefs.fit ? ` I’ve noted a ${prefs.fit} fit preference on this browser.` : '';
      return result(`For sizing, I won’t guess from height or weight alone. Compare garment measurements with a piece you already like, then choose based on the intended silhouette.${saved} Product-specific measurements should always take priority.`, [{ label: 'Open size & fit guide', href: routes.size }], [], 'CULM fit guidance');
    }

    if (q.includes('style') || q.includes('occasion') || q.includes('dinner') || q.includes('office') || q.includes('work') || q.includes('travel') || q.includes('wedding') || q.includes('evening')) {
      return result('Start with the use, then reduce choices: for work, favour restrained colour and repeatable proportion; for warm evenings, prioritise a breathable cloth and easy silhouette; for travel, choose something you can rewear and care for simply. I can search the catalogue using the exact occasion and material direction you give me.', [{ label: 'Search this request', href: searchHref(message) }, { label: 'Browse catalogue', href: routes.shop }], [], 'CULM styling guidance');
    }

    if (q.includes('wardrobe') || q.includes('build my') || q.includes('capsule') || q.includes('smaller wardrobe')) {
      return result('Build by roles, not quantity: one quiet repeat-wear piece, one textural or tonal alternative, and one distinct garment for a different use. Add another piece only when it solves a new role. This keeps the edit deliberate rather than turning recommendations into more consumption.', [{ label: 'Browse the edit', href: routes.shop }, { label: 'CULM Journal', href: routes.journal }], [], 'CULM wardrobe edit');
    }

    if (q.includes('will i wear') || q.includes('do i need') || q.includes('should i buy') || q.includes('worth buying')) {
      return result('Use three checks: Does it serve a role you do not already cover? Can you picture at least three real wears in the next month? Does its care and fit suit how you actually live? If two answers are no, leaving it is probably the better choice.', [{ label: 'Size guide', href: routes.size }, { label: 'Materials & making', href: routes.materials }], [], 'Purchase decision check');
    }

    if (q.includes('hemp') || q.includes('linen') || q.includes('ramie') || q.includes('banana') || q.includes('bamboo') || q.includes('fibre') || q.includes('fiber') || q.includes('material') || q.includes('natural')) {
      let focus = 'Natural fibres differ by plant source, extraction, spinning, weave and finishing. A fibre name alone does not prove a garment’s environmental impact.';
      if (q.includes('hemp') && q.includes('linen')) focus = 'Hemp and linen are both bast-fibre routes, but the plant source, processing, yarn and finishing change how the finished cloth feels and behaves. Compare the actual garment composition and construction, not only the fibre name.';
      if (q.includes('bamboo')) focus = '“Bamboo fabric” often refers to regenerated cellulose such as viscose made from bamboo feedstock. The label should distinguish the fibre/process rather than treating the plant name as the whole material story.';
      return result(focus, [{ label: 'Materials & making', href: routes.materials }, { label: 'CULM Journal', href: routes.journal }], [], 'Material guidance');
    }

    if (q.includes('care') || q.includes('wash') || q.includes('iron') || q.includes('stain') || q.includes('repair') || q.includes('mend') || q.includes('shrink')) {
      return result('Follow the garment-specific care label first. In general, use the gentlest process the record permits, avoid unnecessary heat, address stains and damage early, and repair before replacing when practical. I will not invent a wash temperature or chemistry that the garment page does not state.', [{ label: 'FAQ', href: routes.faq }, { label: 'Contact CULM', href: routes.contact }], [], 'Care guidance');
    }

    if (q.includes('trace') || q.includes('provenance') || q.includes('origin') || q.includes('mill') || q.includes('field') || q.includes('where made') || q.includes('where is it from')) {
      return result('CULM’s traceability rule is simple: verified information is named; unavailable information stays pending or unknown. A product should not gain a farm, mill, artisan or sustainability story just because the story sounds desirable.', [{ label: 'Materials & making', href: routes.materials }, { label: 'Contact CULM', href: routes.contact }], [], 'Traceability rule');
    }

    if (q.includes('journal') || q.includes('read') || q.includes('learn') || q.includes('article') || q.includes('blog')) {
      return result('Use the CULM Journal as the material library: begin with natural fibres, then go deeper into construction, care, traceability and the philosophy of wearing things longer.', [{ label: 'Open CULM Journal', href: routes.journal }, { label: 'Materials & making', href: routes.materials }], [], 'CULM Journal guide');
    }

    if (q.includes('gift') || q.includes('present')) {
      return result('For a gift, narrow by three things: approximate size, how the person actually dresses, and a budget. If size is uncertain, choose the least fit-sensitive option available or use the size guide before ordering. Give me the recipient’s style and budget and I can turn that into a store search.', [{ label: 'Browse catalogue', href: routes.shop }, { label: 'Size guide', href: routes.size }], [], 'Gift guidance');
    }

    if (q.includes('order') || q.includes('tracking') || q.includes('where is my') || q.includes('delivery') || q.includes('shipping') || q.includes('return') || q.includes('refund') || q.includes('exchange')) {
      if (q.includes('return') || q.includes('refund') || q.includes('exchange')) return result('For return or refund eligibility, use the published Returns & Refunds policy. This storefront assistant does not expose private order data.', [{ label: 'Returns & refunds', href: routes.returns }, { label: 'Account', href: routes.account }, { label: 'Contact', href: routes.contact }], [], 'Policy routing');
      return result('For a specific order, sign in to your customer account to view order information. This storefront assistant does not read private order data from the browser. For delivery terms, use the Shipping Policy.', [{ label: 'Customer account', href: routes.account }, { label: 'Shipping policy', href: routes.shipping }, { label: 'Contact', href: routes.contact }], [], 'Private account routing');
    }

    if (q.includes('faq') || q.includes('question')) return result('The FAQ covers fibres, care, sizing, shipping, returns and ordering. If your question is product-specific, open the product first so I can use its recorded context.', [{ label: 'Open FAQ', href: routes.faq }], [], 'CULM FAQ');

    return result('I can search the live CULM catalogue, recommend related pieces, help with fit and styling, build a smaller wardrobe, compare recently viewed pieces, explain natural fibres, read traceability and care information, guide you through the Journal, help with gifting, or route private order questions safely.', [{ label: 'Search CULM', href: searchHref(message) }, { label: 'CULM Journal', href: routes.journal }], [], 'CULM guidance');
  };

  const normaliseProduct = (p) => {
    const featured = p?.featured_image;
    const image = typeof featured === 'string' ? featured : featured?.url || featured?.src || p?.image || '';
    return {
      title: String(p?.title || 'CULM garment'),
      href: p?.url || (p?.handle ? `/products/${p.handle}` : routes.shop),
      image,
      type: p?.type || p?.product_type || '',
      price: p?.price || ''
    };
  };

  const predictiveSearch = async (query) => {
    if (!featureEnabled('enablePredictive')) return [];
    const url = `${routeRoot}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Shopify search returned ${response.status}`);
    const data = await response.json();
    const resources = data.resources || data.suggestions?.resources || {};
    const products = resources.results?.products || [];
    return products.slice(0, 6).map(normaliseProduct);
  };

  const productRecommendations = async () => {
    const c = pageContext();
    if (!c.productId) return [];
    const url = `${routeRoot}recommendations/products.json?product_id=${encodeURIComponent(c.productId)}&limit=6&intent=related`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
    const data = await response.json();
    return (data.products || []).slice(0, 6).map(normaliseProduct);
  };

  const personalisedQuery = () => {
    const prefs = readPreferences();
    const recent = readRecent();
    const type = recent.find((x) => x.type)?.type || '';
    return [prefs.colour, prefs.fibre, prefs.fit, type].filter(Boolean).join(' ').trim();
  };

  const remoteGuide = async (endpoint, message, mode = 'chat') => {
    if (!endpoint) return null;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode, message, context: pageContext(), history: history.slice(-8), brand: 'CULM',
        instruction: 'Use only supplied storefront facts for product-specific claims. Mark missing fibre, provenance, fit, sustainability, price or order facts as unknown rather than guessing. Never expose private customer or order data unless the request is authenticated server-side.'
      })
    });
    if (!response.ok) throw new Error(`AI service returned ${response.status}`);
    const data = await response.json();
    const text = data.answer || data.message || data.output || data.text;
    if (!text) throw new Error('AI service returned no answer');
    const links = Array.isArray(data.links) ? data.links.filter((x) => x && x.href && x.label).slice(0, 5) : (data.url ? [{ label: data.cta || 'Open', href: data.url }] : []);
    const products = Array.isArray(data.products) ? data.products.slice(0, 6).map(normaliseProduct) : [];
    return result(String(text), links, products, data.source || 'Connected AI · store grounded');
  };

  const remoteVision = async (endpoint, file, message = 'Find the closest CULM pieces to this image') => {
    if (!endpoint) return null;
    const form = new FormData();
    form.append('mode', 'vision');
    form.append('brand', 'CULM');
    form.append('message', message);
    form.append('image', file, file.name || 'reference-image');
    form.append('context', JSON.stringify(pageContext()));
    form.append('instruction', 'Match only against the CULM catalogue supplied by the server. Do not infer fibre, provenance or sustainability from appearance.');
    const response = await fetch(endpoint, { method: 'POST', body: form });
    if (!response.ok) throw new Error(`Visual search returned ${response.status}`);
    const data = await response.json();
    const text = data.answer || data.message || data.output || data.text || 'Visual matches from the CULM catalogue.';
    const links = Array.isArray(data.links) ? data.links.filter((x) => x && x.href && x.label).slice(0, 5) : [];
    const products = Array.isArray(data.products) ? data.products.slice(0, 6).map(normaliseProduct) : [];
    return result(String(text), links, products, data.source || 'Connected visual search');
  };

  const setStatus = (text) => document.querySelectorAll('[data-culm-ai-mode]').forEach((node) => { node.textContent = text; });
  const setSource = (text) => document.querySelectorAll('[data-culm-ai-source]').forEach((node) => { node.textContent = text || ''; });

  const initIntelligence = () => {
    const ai = panel();
    const openers = [...document.querySelectorAll('[data-culm-ai-open]')];
    if (!ai || !openers.length) return;

    const endpoint = ai.dataset.endpoint?.trim() || '';
    const closers = [...ai.querySelectorAll('[data-culm-ai-close]')];
    const form = ai.querySelector('[data-culm-ai-form]');
    const input = ai.querySelector('[data-culm-ai-input]');
    const answer = ai.querySelector('[data-culm-ai-answer]');
    const contextNode = ai.querySelector('[data-culm-ai-context]');
    const imageInput = ai.querySelector('[data-culm-ai-image]');
    const photoButtons = [...ai.querySelectorAll('[data-culm-ai-pick-image]')];
    const voiceButtons = [...ai.querySelectorAll('[data-culm-ai-voice]')];
    const clearButtons = [...ai.querySelectorAll('[data-culm-ai-clear-memory]')];
    let previousFocus = null;
    let busy = false;

    const c = pageContext();
    if (contextNode) {
      if (c.product) contextNode.textContent = `Current garment · ${c.product}${c.price ? ` · ${c.price}` : ''}`;
      else if (c.article) contextNode.textContent = `Current Journal article · ${c.article}`;
      else if (c.collection) contextNode.textContent = `Current collection · ${c.collection}`;
      else contextNode.textContent = 'CULM · catalogue / materials / journal / care';
    }

    setStatus(endpoint ? 'Connected AI' : 'Store grounded');

    const focusable = () => [...ai.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
    const open = (opener) => {
      previousFocus = opener || document.activeElement;
      ai.classList.add('is-open'); ai.setAttribute('aria-hidden', 'false'); document.body.classList.add('culm-ai-open');
      requestAnimationFrame(() => input?.focus());
    };
    const close = () => {
      ai.classList.remove('is-open'); ai.setAttribute('aria-hidden', 'true'); document.body.classList.remove('culm-ai-open'); previousFocus?.focus?.();
    };

    const render = (data, prefix = '') => {
      if (!answer) return;
      answer.replaceChildren();
      const p = document.createElement('p'); p.textContent = `${prefix}${data.text}`; answer.appendChild(p);

      if (data.products?.length) {
        const grid = document.createElement('div'); grid.className = 'culm-ai-products';
        data.products.forEach((product) => {
          const a = document.createElement('a'); a.className = 'culm-ai-product'; a.href = product.href || routes.shop;
          if (product.image) { const img = document.createElement('img'); img.src = product.image; img.alt = product.title; img.loading = 'lazy'; a.appendChild(img); }
          const copy = document.createElement('span'); copy.className = 'culm-ai-product__copy';
          const title = document.createElement('strong'); title.textContent = product.title;
          const meta = document.createElement('small'); meta.textContent = product.type || 'View garment';
          copy.append(title, meta); a.appendChild(copy); grid.appendChild(a);
        });
        answer.appendChild(grid);
      }

      if (data.links?.length) {
        const box = document.createElement('div'); box.className = 'culm-ai-actions';
        data.links.slice(0, 5).forEach((link) => { const a = document.createElement('a'); a.href = link.href; a.textContent = link.label; box.appendChild(a); });
        answer.appendChild(box);
      }
      setSource(data.source || '');
    };

    const looksLikeSearch = (q) => /\b(find|show me|looking for|search|shop|under|recommend something|for me)\b/i.test(q);
    const looksLikeRelated = (q) => /\b(similar|related|recommendations?|alternative|like this)\b/i.test(q);

    const ask = async (message, mode = 'chat') => {
      const clean = String(message || '').trim();
      if (!clean || busy) return;
      if (input) input.value = clean;
      rememberPreferences(clean);
      busy = true;
      if (answer) answer.textContent = 'Reading CULM records…';
      setSource('');
      setStatus(endpoint ? 'Connected AI · thinking' : 'Store grounded · reading');

      try {
        const context = pageContext();
        if ((mode === 'related' || looksLikeRelated(clean)) && context.productId) {
          const products = await productRecommendations();
          const data = products.length
            ? result('These are related pieces returned by Shopify for the garment you are viewing. Open a piece to confirm its actual material, fit and construction record.', [], products, 'Shopify related-product recommendations')
            : result('No related products are currently returned for this garment.', [{ label: 'Browse catalogue', href: routes.shop }], [], 'Shopify related-product recommendations');
          render(data); history.push({ role: 'user', content: clean }, { role: 'assistant', content: data.text }); return;
        }

        if (mode === 'personalised' || /\b(for me|my edit|personalised|personalized)\b/i.test(clean)) {
          const q = personalisedQuery();
          if (q) {
            const products = await predictiveSearch(q);
            const prefs = readPreferences();
            const note = [prefs.fit, prefs.fibre, prefs.colour].filter(Boolean).join(' · ');
            const data = products.length
              ? result(`A small edit using preferences saved on this browser${note ? `: ${note}` : ''}. These are live catalogue results; open each product to confirm the record.`, [], products, 'On-device preferences + Shopify search')
              : result('I have some local preferences, but Shopify search did not return a matching product right now.', [{ label: 'Browse catalogue', href: routes.shop }], [], 'On-device preferences + Shopify search');
            render(data); history.push({ role: 'user', content: clean }, { role: 'assistant', content: data.text }); return;
          }
          render(result('Tell me a fit direction, fibre or colour you prefer first — for example “relaxed hemp in olive”. I can remember those simple preferences on this browser and use them for a later edit.', [], [], 'On-device preference memory')); return;
        }

        if (mode === 'search' || looksLikeSearch(clean)) {
          if (endpoint) {
            try { const remote = await remoteGuide(endpoint, clean, 'search'); if (remote) { render(remote); history.push({ role: 'user', content: clean }, { role: 'assistant', content: remote.text }); return; } } catch (_) {}
          }
          const products = await predictiveSearch(clean);
          const data = products.length
            ? result('I found these live CULM catalogue results from your request. Open a piece to confirm its material, fit, price and provenance record.', [{ label: 'See full search', href: searchHref(clean) }], products, 'Shopify predictive search')
            : result('I could not find a close live product match from those words. Try a shorter request using garment type, colour, fibre or fit.', [{ label: 'Open full search', href: searchHref(clean) }, { label: 'Browse catalogue', href: routes.shop }], [], 'Shopify predictive search');
          render(data); history.push({ role: 'user', content: clean }, { role: 'assistant', content: data.text }); return;
        }

        if (endpoint) {
          try { const remote = await remoteGuide(endpoint, clean, mode); if (remote) { render(remote); history.push({ role: 'user', content: clean }, { role: 'assistant', content: remote.text }); return; } }
          catch (_) { /* truthful local fallback below */ }
        }

        const local = localGuide(clean); render(local, endpoint ? 'Connected AI was unavailable. ' : '');
        history.push({ role: 'user', content: clean }, { role: 'assistant', content: local.text });
      } catch (_) {
        const fallback = localGuide(clean); render(fallback, 'Live catalogue lookup was unavailable. ');
      } finally {
        busy = false; setStatus(endpoint ? 'Connected AI' : 'Store grounded');
      }
    };

    const openAndAsk = (opener, message, mode = 'chat') => { open(opener); window.setTimeout(() => ask(message, mode), 80); };

    openers.forEach((b) => b.addEventListener('click', () => open(b)));
    closers.forEach((b) => b.addEventListener('click', close));
    ai.querySelectorAll('[data-culm-ai-question]').forEach((b) => b.addEventListener('click', () => ask(b.dataset.culmAiQuestion || b.textContent, b.dataset.culmAiMode || 'chat')));
    document.querySelectorAll('[data-culm-ai-action]').forEach((b) => b.addEventListener('click', () => openAndAsk(b, b.dataset.culmAiAction || b.textContent, b.dataset.culmAiMode || 'chat')));
    form?.addEventListener('submit', (event) => { event.preventDefault(); ask(input?.value || ''); });

    photoButtons.forEach((b) => b.addEventListener('click', () => imageInput?.click()));
    imageInput?.addEventListener('change', async () => {
      const file = imageInput.files?.[0]; if (!file) return;
      open(imageInput);
      if (!endpoint) {
        render(result('Photo search is ready in the interface, but actual image matching requires the secure AI proxy endpoint. Your selected image has not been uploaded or analysed.', [], [], 'Visual search · secure connection required'));
        imageInput.value = ''; return;
      }
      if (answer) answer.textContent = 'Reading the reference image…';
      setStatus('Connected AI · visual search');
      try { const data = await remoteVision(endpoint, file); render(data); }
      catch (_) { render(result('The connected visual-search service could not process this image. No match has been invented.', [], [], 'Visual search unavailable')); }
      finally { imageInput.value = ''; setStatus('Connected AI'); }
    });

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition || !featureEnabled('enableVoice')) voiceButtons.forEach((b) => { b.disabled = true; b.setAttribute('aria-disabled', 'true'); b.title = 'Voice input is not supported in this browser'; });
    else voiceButtons.forEach((b) => b.addEventListener('click', () => {
      const recognition = new SpeechRecognition();
      recognition.lang = document.documentElement.lang || 'en-IN'; recognition.interimResults = false; recognition.maxAlternatives = 1;
      setStatus('Listening…');
      recognition.onresult = (event) => { const transcript = event.results?.[0]?.[0]?.transcript || ''; if (transcript) { if (input) input.value = transcript; ask(transcript); } };
      recognition.onerror = () => { render(result('Voice input was not available. You can type the same request below.', [], [], 'Browser voice input')); setStatus(endpoint ? 'Connected AI' : 'Store grounded'); };
      recognition.onend = () => { if (!busy) setStatus(endpoint ? 'Connected AI' : 'Store grounded'); };
      try { recognition.start(); } catch (_) {}
    }));

    clearButtons.forEach((b) => b.addEventListener('click', () => {
      try { localStorage.removeItem(recentKey); localStorage.removeItem(preferenceKey); } catch (_) {}
      history.length = 0;
      render(result('Recent-piece history and simple fit, fibre and colour preferences have been cleared from this browser.', [], [], 'Browser memory cleared'));
    }));

    document.addEventListener('keydown', (event) => {
      if (!ai.classList.contains('is-open')) return;
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const items = focusable(); if (!items.length) return;
      if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0].focus(); }
    });
  };

  const setLowImpact = (enabled, persist = true) => {
    root.classList.toggle('culm-low-impact', enabled);
    document.querySelectorAll('[data-culm-impact-toggle]').forEach((b) => b.setAttribute('aria-pressed', enabled ? 'true' : 'false'));
    document.querySelectorAll('[data-culm-impact-label]').forEach((n) => { n.textContent = enabled ? 'Low impact on' : 'Low impact'; });
    document.querySelectorAll('[data-culm-impact-state]').forEach((n) => { n.textContent = enabled ? 'Low-impact display is active.' : 'Standard visual mode is active.'; });
    if (enabled) document.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });
    if (persist) try { localStorage.setItem('culm-low-impact', enabled ? '1' : '0'); } catch (_) {}
  };

  const initImpactControls = () => {
    setLowImpact(root.classList.contains('culm-low-impact'), false);
    document.querySelectorAll('[data-culm-impact-toggle]').forEach((b) => b.addEventListener('click', () => setLowImpact(!root.classList.contains('culm-low-impact'))));
  };

  const initImmersive = () => {
    if (reducedMotion.matches || coarsePointer.matches || root.classList.contains('culm-low-impact')) return;
    document.querySelectorAll('[data-culm-immersive-stage]').forEach((stage) => {
      const move = (event) => {
        const box = stage.getBoundingClientRect(); const x = (event.clientX - box.left) / box.width - .5; const y = (event.clientY - box.top) / box.height - .5;
        stage.style.setProperty('--culm-ry', `${x * 9}deg`); stage.style.setProperty('--culm-rx', `${-y * 8}deg`); stage.style.setProperty('--culm-mx', `${x * 34}px`); stage.style.setProperty('--culm-my', `${y * 34}px`);
      };
      const reset = () => ['--culm-ry','--culm-rx','--culm-mx','--culm-my'].forEach((p, i) => stage.style.setProperty(p, i < 2 ? '0deg' : '0px'));
      stage.addEventListener('pointermove', move, { passive: true }); stage.addEventListener('pointerleave', reset, { passive: true });
    });
  };

  const initFilm = () => {
    document.querySelectorAll('[data-culm-film]').forEach((film) => {
      const trigger = film.querySelector('[data-culm-film-play]'); if (!trigger) return;
      trigger.addEventListener('click', () => {
        const nativeVideo = film.querySelector('video');
        if (nativeVideo) { nativeVideo.controls = true; nativeVideo.play().catch(() => {}); trigger.closest('.culm-film__poster')?.remove(); return; }
        const kind = trigger.dataset.culmVideoKind, id = trigger.dataset.culmVideoId; if (!kind || !id) return;
        const iframe = document.createElement('iframe'); iframe.title = 'CULM process film'; iframe.loading = 'lazy'; iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen'; iframe.allowFullscreen = true;
        if (kind === 'youtube') iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
        if (kind === 'vimeo') iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1&dnt=1`;
        if (!iframe.src) return; film.querySelector('.culm-film__frame')?.appendChild(iframe); trigger.closest('.culm-film__poster')?.remove();
      });
    });
  };

  const ready = () => { rememberCurrentProduct(); initImpactControls(); initImmersive(); initFilm(); initIntelligence(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready); else ready();
})();